/**
 * A countdown that is **display only**.
 *
 * The server owns every deadline. This ticks against the server's clock — the
 * offset is measured on every snapshot — so a candidate whose machine clock is
 * wrong still sees an honest number, and reaching zero here does not end
 * anything. It asks the server what happened, and the server decides.
 */

import { useEffect, useRef, useState } from 'react';

import { formatDuration } from '../../lib/format';

export default function Countdown({
  deadlineAt,
  clockOffsetMs,
  onExpire,
  label,
  size = 'normal',
}: {
  deadlineAt: number | null;
  /** serverNow − clientNow, so a skewed client clock still shows the truth. */
  clockOffsetMs: number;
  /** Fired once, when the displayed time reaches zero. */
  onExpire?: () => void;
  label?: string;
  size?: 'compact' | 'normal' | 'large';
}) {
  const [remaining, setRemaining] = useState(() =>
    deadlineAt === null ? null : deadlineAt - (Date.now() + clockOffsetMs),
  );
  const fired = useRef(false);

  useEffect(() => {
    fired.current = false;
  }, [deadlineAt]);

  useEffect(() => {
    if (deadlineAt === null) {
      setRemaining(null);
      return;
    }

    const tick = () => {
      const left = deadlineAt - (Date.now() + clockOffsetMs);
      setRemaining(left);
      if (left <= 0 && !fired.current) {
        fired.current = true;
        onExpire?.();
      }
    };

    tick();
    const timer = setInterval(tick, 500);
    return () => clearInterval(timer);
  }, [deadlineAt, clockOffsetMs, onExpire]);

  if (remaining === null) return null;

  const seconds = Math.max(0, Math.round(remaining / 1000));
  const urgent = seconds <= 30;
  const warning = seconds <= 120;

  const tone = urgent ? 'text-danger' : warning ? 'text-warn' : 'text-ink/60';

  return (
    <span
      className={`inline-flex items-baseline gap-2 font-code tabular-nums ${tone}`}
      // Announced sparingly: a live region that reads every second would make
      // the page unusable with a screen reader.
      role="timer"
      aria-live={urgent ? 'assertive' : 'off'}
    >
      {label && size !== 'compact' && (
        <span className="text-[10px] uppercase tracking-[0.18em] text-ink/30">{label}</span>
      )}
      <span
        className={
          size === 'large' ? 'text-5xl tracking-tight' : size === 'compact' ? 'text-sm' : 'text-base'
        }
      >
        {formatDuration(remaining)}
      </span>
    </span>
  );
}
